import jwtDecode from "jwt-decode";
import posthog from "posthog-js";

import { getCurrentAccountFromSettings } from "@/hooks/use-current-account";
import { pickMainBaseSubscription, resolvePlanKeyFromProductName } from "@/lib/plans";
import { initPostHog, resetPostHog } from "@/lib/posthog";
import {
  resolveGroupsForAccount,
  resolveUserPersona,
  type UserPersona,
} from "@/lib/user-type-label";
import Client from "@/services/client";
import Settings from "@/services/settings";

import type { SubscriptionPlan } from "@/queries/subscriptions";

const TOKEN_KEY = "TOKEN_LOGIN";
const FULL_REFRESH_TTL_MS = 60_000;
const DEBOUNCE_MS = 2_000;

const INVALID_SUBSCRIPTION_STATUSES = new Set([
  "canceled",
  "cancelled",
  "past_due",
  "incomplete",
  "incomplete_expired",
  "unpaid",
  "paused",
]);

const FREE_PLAN_KEYS = new Set(["gamefik_free", ""]);

interface DecodedToken {
  id?: string | number;
  userId?: string | number;
  uuid?: string;
  email?: string;
  name?: string;
  accountId?: string | number;
  [key: string]: unknown;
}

type PostHogProps = Record<string, unknown>;

let lastFullRefreshAt = 0;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let inflightFull: Promise<void> | null = null;
let lastIdentifiedUserId: string | null = null;
let enrichmentSnapshot: PostHogProps = {};

const client = new Client();

function phDebug(...args: unknown[]) {
  if (typeof window === "undefined") return;
  try {
    if (localStorage.getItem("gamefik_ph_debug") === "1") {
      console.info("[posthog-enrichment]", ...args);
    }
  } catch {
    // ignore
  }
}

export function getPostHogEnrichmentSnapshot(): PostHogProps {
  return { ...enrichmentSnapshot };
}

export function clearPostHogEnrichmentCache(): void {
  lastFullRefreshAt = 0;
  lastIdentifiedUserId = null;
  enrichmentSnapshot = {};
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  inflightFull = null;
}

function decodeToken(token: string): DecodedToken | null {
  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
}

function resolvePlanKeyFromSubscription(activeSub?: SubscriptionPlan): string | null {
  return resolvePlanKeyFromProductName(activeSub?.productName);
}

function planKeyToTier(planKey: string | null | undefined): string {
  if (!planKey) return "unknown";
  if (planKey === "gamefik_free") return "free";
  if (planKey.includes("epic")) return "epic";
  if (planKey.includes("shiny")) return "shiny";
  if (planKey.includes("starter")) return "starter";
  if (planKey.includes("pro")) return "pro";
  if (planKey.includes("enterprise")) return "enterprise";
  if (planKey === "pro" || planKey === "legacy_pro") return "legacy_pro";
  return "unknown";
}

/**
 * Data de criação da conta em ISO — base da condição "unidades criadas a
 * partir de 2026-06-01" nas flags do novo sistema. Usa account.createdAt e,
 * na falta dele, o timestamp embutido no ObjectId (_id) do Mongo.
 *
 * ⚠️ SINCRONIA Manager ↔ App: esta prop de group alimenta os 6 flags do
 * "novo sistema" (manager-new-ui-ab + nova-navbar/trilhas/check-in-diario/
 * mural-escola/agenda-e-news), que devem manter SEMPRE as mesmas condições.
 * Ver readme/POSTHOG_MANAGER_APP_CONTEXT.md antes de alterar.
 */
function resolveAccountCreatedAt(account: { _id?: unknown; createdAt?: unknown } | null | undefined): string | null {
  const created = (account as { createdAt?: string })?.createdAt;
  if (created) {
    const d = new Date(created);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  const id = account?._id ? String(account._id) : null;
  if (id && /^[0-9a-f]{24}$/i.test(id)) {
    return new Date(parseInt(id.slice(0, 8), 16) * 1000).toISOString();
  }
  return null;
}

function resolveRolePrimary(groups: string[]): string {
  if (groups.includes("financial_admin")) return "financial_admin";
  if (groups.includes("admin")) return "admin";
  if (groups.includes("secretary")) return "secretary";
  if (groups.includes("professor_school_global")) return "professor_school_global";
  if (groups.includes("teacher")) return "teacher";
  return groups[0] || "unknown";
}

function limitDisplay(value: unknown): number | string | null {
  if (value === null || value === undefined) return "unlimited";
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  return null;
}

function usagePct(usage: number | null | undefined, limit: unknown): number | null {
  if (usage == null || typeof limit !== "number" || limit <= 0) return null;
  return Math.round((usage / limit) * 100);
}

function normalizeMrrCents(amount: number, interval: string, intervalCount: number): number {
  const count = intervalCount > 0 ? intervalCount : 1;
  const cents = Math.round(amount);
  if (interval === "year") return Math.round(cents / (12 * count));
  if (interval === "month") return Math.round(cents / count);
  return cents;
}

function buildTier0(token: string): {
  userId: string;
  person: PostHogProps;
  groupKey: string | null;
  group: PostHogProps;
  isNmg: boolean;
  accountId: string | null;
} | null {
  const decoded = decodeToken(token);
  if (!decoded) return null;

  const userId = String(decoded.id ?? decoded.userId ?? decoded.uuid ?? decoded.email ?? "");
  if (!userId) return null;

  const settings = Settings.getSettings();
  const account = getCurrentAccountFromSettings() ?? settings?.accountsDetails?.[0];
  const userData = settings?.userData;
  const accountId = account?._id ? String(account._id) : decoded.accountId ? String(decoded.accountId) : null;
  const worldId = account?.world ?? settings?.worldId ?? null;
  const isNmg = Boolean(account?.["new-manager-global"]);
  const signupProfile = account?.signupProfile;
  const signupPlanKey = account?.signupPlanKey;
  const isB2b = signupProfile === "school";

  const groups = resolveGroupsForAccount(userData, accountId);
  const persona: UserPersona = resolveUserPersona({
    user: userData,
    account,
    persona: (userData as { persona?: UserPersona })?.persona,
  });

  const isOwner =
    Boolean(userData?._id && (account as { owner?: string })?.owner) &&
    String((account as { owner?: string })?.owner) === String(userData._id);

  const stripe = account?.stripe as { customerId?: string } | undefined;
  const stripeMoney = (account as { stripeMoney?: { moneyPaid?: number } })?.stripeMoney;
  const roleMap: Record<string, string> = {
    teacher: "professor",
    school: "diretor",
  };

  const person: PostHogProps = {
    email: decoded.email ?? userData?.email,
    name: decoded.name ?? userData?.name,
    user_id: userId,
    account_id: accountId,
    world_id: worldId,
    is_nmg: isNmg,
    product_line: isNmg ? "nmg" : "legacy",
    persona,
    signup_profile: signupProfile ?? null,
    signup_plan_key: signupPlanKey ?? null,
    role_primary: resolveRolePrimary(groups),
    effective_groups: groups,
    is_admin: groups.includes("admin") || Boolean(settings?.isAdmin),
    is_financial_admin: groups.includes("financial_admin"),
    is_teacher: groups.includes("teacher") || groups.includes("professor_school_global"),
    is_account_owner: isOwner,
    is_school_linked_teacher: persona === "professor_school_global",
    accounts_count: settings?.accountsDetails?.length ?? 0,
    language:
      userData?.preferredLanguage ??
      (typeof navigator !== "undefined" ? navigator.language : undefined),
    user_created_at: userData?.createdAt ?? null,
    verified: userData?.verified ?? null,
    is_complete_register: settings?.isCompleteRegister ?? null,
    setup_completed: settings?.setup_completed ?? null,
    account_type: isB2b ? "b2b" : "b2c",
    school_id: isB2b ? accountId : null,
    plan_type: signupPlanKey ?? (account?.isPro ? "pro" : "free"),
    role: signupProfile ? roleMap[signupProfile] ?? signupProfile : undefined,
    created_at: userData?.createdAt ?? undefined,
    signup_plan_cycle: account?.signupPlanCycle ?? null,
    signup_currency: account?.signupCurrency ?? null,
    signup_plan_completed_at: account?.signupPlanCompletedAt ?? null,
    cancel_at_period_end: (account as { cancelAtPeriodEnd?: boolean })?.cancelAtPeriodEnd ?? null,
    has_stripe_customer: Boolean(stripe?.customerId),
    is_pro: account?.isPro ?? false,
    purchases_count: settings?.purchasesCounter ?? null,
    missoes2_enabled: settings?.missoes2 ?? null,
    missoes2_ia_proofs: settings?.missoes2ComprovantesComIa ?? null,
    shopping_disabled: settings?.shopping_disabled ?? null,
    game_controlado: settings?.game_controlado ?? null,
  };

  const group: PostHogProps = {
    account_id: accountId,
    world_id: worldId,
    account_name: account?.name ?? settings?.schoolName ?? null,
    account_created_at: resolveAccountCreatedAt(account),
    is_nmg: isNmg,
    signup_profile: signupProfile ?? null,
    signup_plan_key: signupPlanKey ?? null,
    billing_cycle: account?.signupPlanCycle ?? null,
    billing_currency: account?.signupCurrency ?? null,
    setup_completed: settings?.setup_completed ?? null,
    has_stripe_customer: Boolean(stripe?.customerId),
    stripe_status: (account as { stripeStatus?: string })?.stripeStatus ?? null,
    ltv_cents: typeof stripeMoney?.moneyPaid === "number" ? stripeMoney.moneyPaid : null,
    purchases_count: settings?.purchasesCounter ?? null,
    is_pro: account?.isPro ?? false,
  };

  return {
    userId,
    person,
    groupKey: worldId ? String(worldId) : null,
    group,
    isNmg,
    accountId,
  };
}

function applyToPostHog(userId: string, person: PostHogProps, groupKey: string | null, group: PostHogProps): void {
  if (typeof window === "undefined") return;

  posthog.identify(userId, person);
  if (groupKey) {
    posthog.group("account", groupKey, group);
  }

  posthog.register({
    is_nmg: person.is_nmg,
    persona: person.persona,
    effective_plan_key: person.effective_plan_key ?? person.signup_plan_key,
    product_line: person.product_line,
    app_section: "manager_b2c",
  });

  enrichmentSnapshot = { ...person, ...group };
  lastIdentifiedUserId = userId;
}

async function fetchTier1(
  tier0: NonNullable<ReturnType<typeof buildTier0>>
): Promise<{ person: PostHogProps; group: PostHogProps }> {
  const { isNmg, accountId, person: basePerson, group: baseGroup } = tier0;
  const account = getCurrentAccountFromSettings();
  const signupProfile = account?.signupProfile;
  const hasStripe = Boolean(basePerson.has_stripe_customer);

  const tasks: {
    key: string;
    run: () => Promise<unknown>;
  }[] = [];

  if (isNmg) {
    tasks.push({
      key: "limits",
      run: () => client.getCurrentPlanLimits(),
    });
    tasks.push({
      key: "iaCredits",
      run: () => client.authenticatedRequest("GET", "ia-credits/remaining"),
    });
  }

  if (isNmg || hasStripe) {
    tasks.push({
      key: "subscriptions",
      run: () => client.authenticatedRequest("GET", "subscriptions/active"),
    });
  }

  tasks.push({
    key: "userInfo",
    run: () => client.authenticatedRequest("GET", "users/info"),
  });

  if (isNmg && signupProfile === "school") {
    tasks.push({
      key: "accountInfo",
      run: () => client.authenticatedRequest("GET", "users/account/info"),
    });
  }

  const results = await Promise.allSettled(tasks.map((t) => t.run()));
  const byKey: Record<string, unknown> = {};
  tasks.forEach((t, i) => {
    if (results[i].status === "fulfilled") {
      byKey[t.key] = (results[i] as PromiseFulfilledResult<unknown>).value;
    }
  });

  const person: PostHogProps = { ...basePerson };
  const group: PostHogProps = { ...baseGroup };

  const userInfo = byKey.userInfo as Record<string, unknown> | undefined;
  if (userInfo?.persona) {
    person.persona = userInfo.persona;
    person.is_school_linked_teacher = userInfo.persona === "professor_school_global";
  }
  if (Array.isArray(userInfo?.effectiveGroups)) {
    person.effective_groups = userInfo.effectiveGroups;
    person.role_primary = resolveRolePrimary(userInfo.effectiveGroups as string[]);
  }
  if (userInfo?.onboarding_professor_completed != null) {
    person.onboarding_professor_completed = Boolean(userInfo.onboarding_professor_completed);
  }
  const primeirosPassos = userInfo?.primeiros_passos_completed;
  if (Array.isArray(primeirosPassos)) {
    person.primeiros_passos_count = primeirosPassos.length;
  }

  const limits = byKey.limits as Record<string, unknown> | undefined;
  let effectivePlanKey =
    (person.signup_plan_key as string | null) ?? null;
  if (limits) {
    if (limits.planKey) {
      effectivePlanKey = String(limits.planKey);
      person.effective_plan_key = effectivePlanKey;
      group.effective_plan_key = effectivePlanKey;
    }
    if (limits.persona) {
      person.persona = limits.persona;
      group.persona = limits.persona;
    }
    person.plan_tier = planKeyToTier(effectivePlanKey);
    const usage = limits.usage as Record<string, number | null> | undefined;
    const lim = limits.limits as Record<string, unknown> | undefined;
    if (usage) {
      group.students_count = usage.students ?? null;
      group.classes_count = usage.classes ?? null;
      group.teachers_count = usage.teachers ?? null;
      group.activities_mtd = usage.activities ?? null;
    }
    if (lim) {
      group.limit_students = limitDisplay(lim.studentsTotal);
      group.limit_classes = limitDisplay(lim.classesActive);
      const studentLimit = typeof lim.studentsTotal === "number" ? lim.studentsTotal : null;
      const studentsUsed = typeof usage?.students === "number" ? usage.students : null;
      group.student_limit_pct = usagePct(studentsUsed, studentLimit);
      group.is_at_student_limit =
        studentLimit != null && studentsUsed != null && studentsUsed >= studentLimit;
    }
    const features = limits.features as Record<string, boolean> | undefined;
    if (features) {
      group.can_manage_billing = features.canManageBilling ?? null;
      person.can_manage_billing = features.canManageBilling ?? null;
    }
  }

  const subsResponse = byKey.subscriptions as { data?: { subscriptions?: SubscriptionPlan[] } } | undefined;
  const subs = subsResponse?.data?.subscriptions ?? [];
  // REGRA CANONICA (AGENTS.md secao 16): props de plano vêm do plano-base (varre itens/
  // subs), nao subs[0] — que pode ser um add-on/IA. Fallback p/ subs[0] só no estado raro
  // "sem plano-base" (ex.: conta Free com add-on avulso).
  const mainBaseSub = pickMainBaseSubscription(subs);
  const activeSub = mainBaseSub?.subscription ?? subs[0];
  if (activeSub) {
    group.has_stripe_subscription = true;
    group.subscription_status = activeSub.status;
    group.subscription_is_trialing = activeSub.status === "trialing";
    group.subscription_is_problematic = INVALID_SUBSCRIPTION_STATUSES.has(activeSub.status);
    group.cancel_at_period_end = activeSub.cancelAtPeriodEnd ?? group.cancel_at_period_end;
    group.mrr_cents = normalizeMrrCents(activeSub.amount, activeSub.interval, activeSub.intervalCount);
    group.billing_currency = activeSub.currency ?? group.billing_currency;
    group.billing_cycle =
      activeSub.interval === "year" ? "year" : activeSub.interval === "month" ? "month" : group.billing_cycle;

    const fromSub = mainBaseSub?.planKey ?? resolvePlanKeyFromSubscription(activeSub);
    if (fromSub) {
      effectivePlanKey = fromSub;
      person.effective_plan_key = fromSub;
      group.effective_plan_key = fromSub;
      person.plan_tier = planKeyToTier(fromSub);
    }

    let packCount = 0;
    let aiUnlimited = false;
    for (const sub of subs) {
      for (const item of sub.items || []) {
        if (item.addonType === "student_pack") {
          packCount += Number(item.quantity || 0);
        }
        if (item.addonType === "ai_unlimited") {
          aiUnlimited = true;
        }
      }
    }
    group.addon_student_packs = packCount;
    person.ia_unlimited_addon = aiUnlimited;
  } else if (isNmg || hasStripe) {
    group.has_stripe_subscription = false;
  }

  const accountInfo = byKey.accountInfo as Record<string, unknown> | undefined;
  if (accountInfo) {
    if (accountInfo.trialDaysRemaining != null) {
      group.trial_days_remaining = Number(accountInfo.trialDaysRemaining);
    }
    if (accountInfo.onboarding_escola_completed != null) {
      group.onboarding_escola_completed = Boolean(accountInfo.onboarding_escola_completed);
    }
    if (accountInfo.setup_completed != null) {
      group.setup_completed = Boolean(accountInfo.setup_completed);
    }
  }

  const ia = byKey.iaCredits as {
    remaining?: number;
    total?: number;
    isUnlimited?: boolean;
  } | undefined;
  if (ia) {
    if (ia.isUnlimited) {
      person.ia_credits_remaining = null;
      person.ia_credits_monthly_limit = null;
      person.ia_credits_pct_used = null;
      person.ia_credits_unlimited = true;
    } else if (typeof ia.remaining === "number" && typeof ia.total === "number" && ia.total > 0) {
      person.ia_credits_remaining = ia.remaining;
      person.ia_credits_monthly_limit = ia.total;
      person.ia_credits_pct_used = Math.round((1 - ia.remaining / ia.total) * 100);
      person.ia_credits_unlimited = false;
    }
  }

  const payingByPlan =
    effectivePlanKey != null && !FREE_PLAN_KEYS.has(effectivePlanKey) && effectivePlanKey !== "gamefik_free";
  group.is_paying = Boolean(
    group.mrr_cents && Number(group.mrr_cents) > 0
      ? true
      : payingByPlan || group.is_pro || person.is_pro
  );

  person.is_free_plan =
    isNmg &&
    person.persona === "nmg_b2c_teacher" &&
    (!effectivePlanKey || effectivePlanKey === "gamefik_free");

  return { person, group };
}

async function runFullEnrichment(tier0: NonNullable<ReturnType<typeof buildTier0>>): Promise<void> {
  try {
    const { person, group } = await fetchTier1(tier0);
    applyToPostHog(tier0.userId, person, tier0.groupKey, group);
    phDebug("full refresh ok", tier0.userId);
  } catch (error) {
    phDebug("full refresh failed", error);
  }
}

export type RefreshPostHogOptions = {
  token?: string | null;
  force?: boolean;
  mode?: "sync" | "full";
};

/**
 * Atualiza person + group no PostHog.
 * - sync: só Tier 0 (Settings + JWT), sem HTTP extra.
 * - full: Tier 0 imediato + Tier 1 se TTL/expirado ou force.
 */
export async function refreshPostHogContext(options?: RefreshPostHogOptions): Promise<void> {
  if (typeof window === "undefined") return;

  initPostHog();

  const token =
    options?.token ??
    (() => {
      try {
        return window.localStorage.getItem(TOKEN_KEY);
      } catch {
        return null;
      }
    })();

  if (!token) {
    resetPostHog();
    clearPostHogEnrichmentCache();
    return;
  }

  const tier0 = buildTier0(token);
  if (!tier0) return;

  const mode = options?.mode ?? "full";
  applyToPostHog(tier0.userId, tier0.person, tier0.groupKey, tier0.group);

  const shouldFull =
    mode === "full" && (options?.force === true || Date.now() - lastFullRefreshAt >= FULL_REFRESH_TTL_MS);

  if (!shouldFull) {
    phDebug("sync only", tier0.userId);
    return;
  }

  if (inflightFull) {
    await inflightFull;
    return;
  }

  inflightFull = runFullEnrichment(tier0).finally(() => {
    inflightFull = null;
    lastFullRefreshAt = Date.now();
  });

  await inflightFull;
}

/** Agenda full refresh com debounce (navegação). */
export function schedulePostHogFullRefresh(options?: Omit<RefreshPostHogOptions, "mode">): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void refreshPostHogContext({ ...options, mode: "full" });
  }, DEBOUNCE_MS);
}

/** Login / signup: identify imediato + enrich completo em seguida. */
export function identifyAndEnrichFromToken(token: string | null | undefined): void {
  if (!token) {
    resetPostHog();
    clearPostHogEnrichmentCache();
    return;
  }
  void refreshPostHogContext({ token, mode: "sync" });
  void refreshPostHogContext({ token, mode: "full", force: true });
}

export function isPostHogIdentified(): boolean {
  return lastIdentifiedUserId != null;
}
